import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppFooter from '../components/shared/AppFooter';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  startAfter,
  doc,
  updateDoc,
  increment,
  where,
  type QueryDocumentSnapshot,
  type DocumentData,
  type QueryConstraint,
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { COLLECTIONS } from '../lib/collections';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';
import { parsePlanSections } from '../app/lib/planPdf';
import Topbar from '../app/components/Topbar';

interface SpotlightPost {
  id: string;
  contentType: string;
  caption: string;
  creatorName: string;
  businessName: string;
  complimentCount: number;
  createdAt: { toDate?: () => Date } | null;
  planText?: string;
}

type FilterType = 'all' | 'business' | 'pitch' | 'story' | 'artwork';

const CONTENT_ICONS: Record<string, string> = {
  pitch: '🎤',
  story: '📖',
  artwork: '🎨',
  business: '🏢',
  default: '✨',
};

const FILTERS: { key: FilterType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'business', label: '🏢 Business' },
  { key: 'pitch', label: '🎤 Pitch' },
  { key: 'story', label: '📖 Story' },
  { key: 'artwork', label: '🎨 Art' },
];

const PAGE_SIZE = 12;

export default function SpotlightPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [posts, setPosts] = useState<SpotlightPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [complimentedIds, setComplimentedIds] = useState<Set<string>>(new Set());
  const [selectedPost, setSelectedPost] = useState<SpotlightPost | null>(null);

  const loadPosts = useCallback(
    async (afterDoc?: QueryDocumentSnapshot<DocumentData>, currentFilter?: FilterType) => {
      try {
        const ref = collection(db, COLLECTIONS.SPOTLIGHT);
        const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc'), limit(PAGE_SIZE)];
        const activeFilter = currentFilter ?? filter;
        if (activeFilter !== 'all') {
          constraints.unshift(where('contentType', '==', activeFilter));
        }
        if (afterDoc) {
          constraints.push(startAfter(afterDoc));
        }
        const q = query(ref, ...constraints);
        const snapshot = await getDocs(q);

        const newPosts = snapshot.docs.map((d) => ({
          id: d.id,
          contentType: d.data().contentType ?? 'default',
          caption: d.data().caption ?? '',
          creatorName: d.data().creatorName ?? 'Anonymous',
          businessName: d.data().businessName ?? '',
          complimentCount: d.data().complimentCount ?? 0,
          createdAt: d.data().createdAt ?? null,
          planText: d.data().planText ?? '',
        }));

        if (afterDoc) {
          setPosts((prev) => [...prev, ...newPosts]);
        } else {
          setPosts(newPosts);
        }

        setLastDoc(snapshot.docs[snapshot.docs.length - 1] ?? null);
        setHasMore(snapshot.docs.length === PAGE_SIZE);
      } catch (error) {
        console.error('Error loading spotlight posts:', error);
      }
    },
    [filter],
  );

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadPosts(undefined, filter);
      setLoading(false);
    };
    init();
  }, [loadPosts, filter]);

  const handleFilterChange = (f: FilterType) => {
    setFilter(f);
    setLastDoc(null);
    setHasMore(true);
  };

  const handleLoadMore = async () => {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    await loadPosts(lastDoc);
    setLoadingMore(false);
  };

  const handleCompliment = async (postId: string) => {
    if (!user || complimentedIds.has(postId)) return;
    setComplimentedIds((prev) => new Set(prev).add(postId));
    setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, complimentCount: p.complimentCount + 1 } : p));
    try {
      const ref = doc(db, COLLECTIONS.SPOTLIGHT, postId);
      await updateDoc(ref, { complimentCount: increment(1) });
    } catch (e) {
      console.error(e);
      setComplimentedIds((prev) => { const next = new Set(prev); next.delete(postId); return next; });
      setPosts((prev) => prev.map((p) => p.id === postId ? { ...p, complimentCount: p.complimentCount - 1 } : p));
    }
  };

  const filteredPosts = search.trim()
    ? posts.filter((p) =>
        p.caption.toLowerCase().includes(search.toLowerCase()) ||
        p.creatorName.toLowerCase().includes(search.toLowerCase()) ||
        p.businessName.toLowerCase().includes(search.toLowerCase())
      )
    : posts;

  if (loading) return <LoadingScreen />;

  return (
    <AnimatedPage>
      <div className="relative min-h-screen pb-12" style={{ backgroundColor: '#0D0F1C' }}>
      <Topbar />
      <div className="px-4 pt-8">
        {/* Dark grid + magenta orb */}
        <div
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px),
              radial-gradient(circle at 50% 55%, rgba(236,72,153,0.18) 0%, rgba(139,92,246,0.08) 38%, transparent 68%)
            `,
            backgroundSize: '40px 40px, 40px 40px, 100% 100%',
          }}
        />
        <div className="relative z-10 mx-auto max-w-5xl">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <h1 className="font-marker text-3xl text-white md:text-4xl">Spotlight</h1>
            <p className="mt-1 text-sm" style={{ color: 'rgba(232,234,240,0.55)' }}>Discover what other young entrepreneurs are building</p>
          </motion.div>

          {/* Search + Publish */}
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search posts…"
              className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none sm:max-w-xs"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
            <button
              onClick={() => navigate('/home/plans')}
              className="min-h-[44px] w-full rounded-xl px-6 py-3 font-marker text-sm font-medium text-white shadow-md transition-colors sm:w-auto"
              style={{ background: 'rgba(245,158,11,0.9)' }}
            >
              Create
            </button>
          </div>

          {/* Filters */}
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => handleFilterChange(f.key)}
                className="min-h-[36px] whitespace-nowrap rounded-full px-4 py-1.5 font-marker text-xs font-medium transition-all"
                style={
                  filter === f.key
                    ? { background: 'rgba(245,158,11,0.2)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.4)' }
                    : { background: 'rgba(255,255,255,0.06)', color: 'rgba(232,234,240,0.6)', border: '1px solid rgba(255,255,255,0.08)' }
                }
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Get Inspired banner */}
          {filteredPosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 rounded-2xl p-4 text-center"
              style={{ background: 'rgba(236,72,153,0.08)', border: '1px solid rgba(236,72,153,0.2)' }}
            >
              <p className="font-marker text-sm" style={{ color: 'rgba(236,72,153,0.9)' }}>
                💡 Get Inspired — {filteredPosts.length} young entrepreneur{filteredPosts.length !== 1 ? 's' : ''} {filteredPosts.length !== 1 ? 'are' : 'is'} sharing!
              </p>
            </motion.div>
          )}

          {/* Feed */}
          {filteredPosts.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-16 text-center">
              <span className="text-5xl">🌟</span>
              <h2 className="mt-4 font-marker text-xl text-white">{search ? 'No matches' : 'No posts yet'}</h2>
              <p className="mt-2 text-sm" style={{ color: 'rgba(232,234,240,0.5)' }}>{search ? 'Try a different search term' : 'Complete your business plan and share it with the world!'}</p>
            </motion.div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <AnimatePresence mode="popLayout">
                  {filteredPosts.map((post, i) => {
                    const hasComplimented = complimentedIds.has(post.id);
                    return (
                      <motion.div
                        key={post.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ delay: Math.min(i * 0.04, 0.4) }}
                        className="group cursor-pointer rounded-2xl transition-all hover:scale-[1.02]"
                        style={{ background: '#161929', border: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => setSelectedPost(post)}
                      >
                        {/* Card header */}
                        <div className="flex items-center gap-2 rounded-t-2xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
                          <span className="text-xl">{CONTENT_ICONS[post.contentType] ?? CONTENT_ICONS.default}</span>
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm font-bold text-white">{post.creatorName}</p>
                            {post.businessName && <p className="truncate text-xs" style={{ color: 'rgba(232,234,240,0.5)' }}>{post.businessName}</p>}
                          </div>
                          <span className="shrink-0 rounded-full px-2 py-0.5 font-marker text-[9px] uppercase" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>{post.contentType}</span>
                        </div>
                        {/* Caption */}
                        <div className="px-4 py-3">
                          <p className="text-sm leading-relaxed line-clamp-4" style={{ color: 'rgba(232,234,240,0.75)' }}>{post.caption || 'No caption'}</p>
                        </div>
                        {/* Footer */}
                        <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                          <span className="text-xs" style={{ color: 'rgba(232,234,240,0.35)' }}>
                            {post.createdAt?.toDate?.()
                              ? post.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : ''}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCompliment(post.id); }}
                            disabled={hasComplimented}
                            className="flex min-h-[32px] items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-all"
                            style={hasComplimented
                              ? { background: 'rgba(139,92,246,0.2)', color: '#A78BFA' }
                              : { background: 'rgba(255,255,255,0.07)', color: 'rgba(232,234,240,0.5)' }}
                          >
                            <motion.span key={post.complimentCount} initial={hasComplimented ? { scale: 1.4 } : false} animate={{ scale: 1 }}>💜</motion.span>
                            {post.complimentCount > 0 && <span>{post.complimentCount}</span>}
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>

              {/* Load more */}
              {hasMore && !search && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="min-h-[44px] rounded-xl px-6 py-3 font-marker text-sm transition-colors disabled:opacity-50"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(232,234,240,0.7)' }}
                  >
                    {loadingMore ? 'Loading…' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>{/* /outer dark bg */}

      {/* Post detail modal */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm"
            style={{ background: 'rgba(0,0,0,0.7)' }}
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              className="flex w-full max-w-2xl flex-col rounded-2xl shadow-2xl"
              style={{ background: '#161929', border: '1px solid rgba(255,255,255,0.1)', maxHeight: '90vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-center gap-3 rounded-t-2xl px-5 py-4" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <span className="text-2xl">{CONTENT_ICONS[selectedPost.contentType] ?? CONTENT_ICONS.default}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white">{selectedPost.creatorName}</p>
                  {selectedPost.businessName && <p className="text-sm" style={{ color: 'rgba(232,234,240,0.5)' }}>{selectedPost.businessName}</p>}
                </div>
                <span className="rounded-full px-2 py-0.5 font-marker text-[9px] uppercase" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                  {selectedPost.contentType}
                </span>
                <button onClick={() => setSelectedPost(null)} className="ml-2 text-xl" style={{ color: 'rgba(232,234,240,0.4)' }}>&times;</button>
              </div>

              {/* Scrollable body: caption + full plan */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {/* Caption */}
                <p className="whitespace-pre-wrap text-base leading-relaxed" style={{ color: 'rgba(232,234,240,0.85)' }}>
                  {selectedPost.caption || 'No caption'}
                </p>

                {/* Full KoKo plan */}
                {selectedPost.planText && (() => {
                  const sections = parsePlanSections(selectedPost.planText);
                  return (
                    <div className="mt-6">
                      <div className="mb-3 flex items-center gap-2">
                        <span className="text-lg">🦊</span>
                        <span className="font-marker text-[11px] uppercase tracking-[0.22em]" style={{ color: '#F59E0B' }}>
                          KoKo's Write-Up
                        </span>
                      </div>
                      {sections.length > 0 ? (
                        <div className="space-y-3">
                          {sections.map((sec, i) => (
                            <div
                              key={`${sec.title}-${i}`}
                              className="rounded-2xl p-4"
                              style={{ background: 'rgba(13,15,28,0.6)', border: '1px solid rgba(255,255,255,0.06)' }}
                            >
                              <div className="font-marker text-[11px] uppercase tracking-[0.22em]" style={{ color: '#10B981' }}>
                                {sec.title}
                              </div>
                              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed" style={{ color: 'rgba(232,234,240,0.85)' }}>
                                {sec.body}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: 'rgba(232,234,240,0.8)' }}>
                          {selectedPost.planText}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Footer */}
              <div className="flex shrink-0 items-center justify-between px-5 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <span className="text-xs" style={{ color: 'rgba(232,234,240,0.35)' }}>
                  {selectedPost.createdAt?.toDate?.()
                    ? selectedPost.createdAt.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                    : ''}
                </span>
                <button
                  onClick={() => handleCompliment(selectedPost.id)}
                  disabled={complimentedIds.has(selectedPost.id)}
                  className="flex min-h-[36px] items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-all"
                  style={complimentedIds.has(selectedPost.id)
                    ? { background: 'rgba(139,92,246,0.2)', color: '#A78BFA' }
                    : { background: 'rgba(255,255,255,0.07)', color: 'rgba(232,234,240,0.55)' }}
                >
                  <motion.span
                    key={posts.find((p) => p.id === selectedPost.id)?.complimentCount}
                    initial={complimentedIds.has(selectedPost.id) ? { scale: 1.4 } : false}
                    animate={{ scale: 1 }}
                  >
                    💜
                  </motion.span>
                  <span>
                    {(posts.find((p) => p.id === selectedPost.id)?.complimentCount ?? selectedPost.complimentCount) > 0
                      ? posts.find((p) => p.id === selectedPost.id)?.complimentCount ?? selectedPost.complimentCount
                      : 'Compliment'}
                  </span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AppFooter />
    </AnimatedPage>
  );
}
